import React, { useState, useEffect, useRef } from 'react';
import { Palette, X, Move, Sun, Moon } from 'lucide-react';
import { motion, useDragControls, AnimatePresence } from 'motion/react';
import { useApp } from '../context/AppContext';

const PRESET_COLORS = [
  '#FF2E93', '#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4'
];

const PRESET_GRADIENTS = [
  { color: '#ec4899', gradient: 'linear-gradient(135deg, #f43f5e 0%, #ec4899 100%)' }, // Rose to Pink
  { color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)' }, // Purple to Indigo
  { color: '#f59e0b', gradient: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)' }, // Amber to Red
  { color: '#10b981', gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }, // Emerald to Green
  { color: '#0ea5e9', gradient: 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)' }, // Sky to Blue
  { color: '#000000', gradient: 'linear-gradient(135deg, #434343 0%, #000000 100%)' }, // Dark Chrome
];

export default function ThemePalette() {
  const { themeColor, themeGradient, setThemeColor, showThemePalette, theme, setTheme } = useApp();
  const [isOpen, setIsOpen] = useState(false);
  const [isIdle, setIsIdle] = useState(false);
  const dragControls = useDragControls();
  const idleTimer = useRef<NodeJS.Timeout>();
  const [windowSize, setWindowSize] = useState({ w: typeof window !== 'undefined' ? window.innerWidth : 1000, h: typeof window !== 'undefined' ? window.innerHeight : 1000 });

  const parseGradientColors = (gradient?: string, baseColor?: string) => {
    if (gradient) {
      const matches = gradient.match(/#[0-9a-fA-F]{6}|#[0-9a-fA-F]{3}/g);
      if (matches && matches.length >= 2) {
        return [matches[0], matches[1]];
      }
    }
    return [baseColor || '#FF2E93', baseColor || '#FF2E93'];
  };

  const handleCustomGradient = (c1: string, c2: string) => {
    setThemeColor(c1, `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`);
  };

  const resetIdle = () => {
    setIsIdle(false);
    if (idleTimer.current) clearTimeout(idleTimer.current);
    if (!isOpen) {
      idleTimer.current = setTimeout(() => setIsIdle(true), 3000);
    }
  };

  useEffect(() => {
    const handleResize = () => setWindowSize({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener('resize', handleResize);
    resetIdle();
    return () => {
      window.removeEventListener('resize', handleResize);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [isOpen, showThemePalette]);

  if (!showThemePalette) return null;

  return (
    <>
      <motion.div
        drag
        dragControls={dragControls}
        dragMomentum={false}
        dragElastic={0.1}
        // Keep it within the screen bounds
        dragConstraints={{ top: 0, left: 0, right: windowSize.w - 40, bottom: windowSize.h - 40 }}
        initial={{ x: windowSize.w - 60, y: windowSize.h / 2 }}
        onDragStart={() => setIsIdle(false)}
        onDragEnd={resetIdle}
        className={`fixed z-[90] transition-opacity duration-500`}
        style={{ left: 0, top: 0, opacity: isIdle && !isOpen ? 0.4 : 1 }}
        onPointerMove={resetIdle}
        onPointerDown={resetIdle}
      >
        <div 
          onPointerDown={(e) => dragControls.start(e)}
          onClick={() => setIsOpen(!isOpen)}
          className={`w-10 h-10 rounded-full cursor-grab active:cursor-grabbing bg-white dark:bg-neutral-900 shadow-[0_8px_30px_rgb(0,0,0,0.15)] border border-neutral-200 dark:border-neutral-800 flex items-center justify-center text-primary transition-transform relative hover:scale-105 active:scale-95 backdrop-blur-sm ${isIdle && !isOpen ? 'opacity-80 scale-90' : ''}`}
          style={{ color: 'var(--theme-color, #FF2E93)' }}
        >
          <Palette className="w-5 h-5 pointer-events-none" />
          <div className="absolute -top-1 -right-1 bg-neutral-800 dark:bg-neutral-200 text-white dark:text-black rounded-full p-0.5 shadow-sm border-2 border-white dark:border-neutral-900 pointer-events-none">
             <Move className="w-2.5 h-2.5 pointer-events-none" />
          </div>
        </div>
      </motion.div>

      <AnimatePresence>
        {isOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            />
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="relative bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 shadow-2xl rounded-2xl p-5 w-full max-w-[320px] mx-auto z-10"
            >
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-sm font-bold text-neutral-800 dark:text-neutral-100 flex items-center gap-2">
                  <Palette className="w-4 h-4 text-primary" style={{ color: 'var(--theme-color, #FF2E93)' }} />
                  {theme === 'dark' ? 'Theme Engine' : 'থিম ইঞ্জিন'}
                </h3>
                <button 
                  onClick={() => setIsOpen(false)} 
                  className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 p-1 bg-neutral-100 dark:bg-neutral-800 rounded-full"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-6">
                {/* Dark / Light Toggle */}
                <div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3 font-medium uppercase tracking-wider">Appearance</p>
                  <div className="flex bg-neutral-100 dark:bg-neutral-800 p-1 rounded-lg">
                    <button
                      onClick={() => setTheme('light')}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-all ${theme === 'light' ? 'bg-white text-neutral-900 shadow-sm' : 'text-neutral-500'}`}
                    >
                      <Sun className="w-4 h-4" /> Light
                    </button>
                    <button
                      onClick={() => setTheme('dark')}
                      className={`flex-1 flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-md transition-all ${theme === 'dark' ? 'bg-neutral-900 text-white shadow-sm' : 'text-neutral-400'}`}
                    >
                      <Moon className="w-4 h-4" /> Dark
                    </button>
                  </div>
                </div>

{/* Solid Colors */}
                <div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3 font-medium uppercase tracking-wider">Solid Colors</p>
                  <div className="grid grid-cols-7 gap-2">
                    {PRESET_COLORS.map(c => (
                      <button
                        key={c}
                        onClick={() => setThemeColor(c)}
                        className={`w-full aspect-square rounded-full shadow-sm transition-transform cursor-pointer hover:scale-110 active:scale-95 flex items-center justify-center ${themeColor === c && !themeGradient ? 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-neutral-900 ring-neutral-800 dark:ring-white scale-110' : ''}`}
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </div>

                {/* Premium Gradients */}
                <div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-3 font-medium uppercase tracking-wider">Premium Gradients</p>
                  <div className="grid grid-cols-6 gap-2">
                    {PRESET_GRADIENTS.map((g, i) => (
                      <button
                        key={i}
                        onClick={() => setThemeColor(g.color, g.gradient)}
                        className={`w-full aspect-square rounded-full shadow-sm transition-transform cursor-pointer hover:scale-110 active:scale-95 ${themeGradient === g.gradient ? 'ring-2 ring-offset-2 ring-offset-white dark:ring-offset-neutral-900 ring-neutral-800 dark:ring-white scale-110' : ''}`}
                        style={{ backgroundImage: g.gradient }}
                      />
                    ))}
                  </div>
                </div>
                
                {/* Custom Styling */}
                <div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-2 font-medium uppercase tracking-wider">Custom Style</p>
                  <div className="space-y-2">
                    {/* Solid Builder */}
                    <div className="flex items-center justify-between gap-2 bg-neutral-50 dark:bg-neutral-800 p-2 rounded-lg border border-neutral-100 dark:border-neutral-700">
                      <span className="text-sm font-medium text-neutral-600 dark:text-neutral-300">Solid Color</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] sm:text-xs text-neutral-500 dark:text-neutral-400 font-medium tracking-wider">{themeColor.toUpperCase()}</span>
                        <input
                          type="color"
                          value={themeColor}
                          onChange={(e) => setThemeColor(e.target.value)}
                          className="w-7 h-7 sm:w-8 sm:h-8 rounded cursor-pointer border-0 bg-transparent p-0"
                        />
                      </div>
                    </div>

                    {/* Gradient Builder */}
                    <div className="flex flex-col gap-2 bg-neutral-50 dark:bg-neutral-800 p-2 rounded-lg border border-neutral-100 dark:border-neutral-700">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-neutral-600 dark:text-neutral-300">Gradient Flow</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 p-1 rounded-md">
                          <input
                            type="color"
                            value={parseGradientColors(themeGradient, themeColor)[0]}
                            onChange={(e) => {
                              const [, c2] = parseGradientColors(themeGradient, themeColor);
                              handleCustomGradient(e.target.value, c2);
                            }}
                            className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent p-0"
                          />
                        </div>
                        <span className="text-[10px] text-neutral-400 font-mono tracking-widest px-2 relative after:content-[''] after:absolute after:h-px after:bg-neutral-300 dark:after:bg-neutral-700 after:left-full after:right-[-10px] after:top-1/2 before:content-[''] before:absolute before:h-px before:bg-neutral-300 dark:before:bg-neutral-700 before:right-full before:left-[-10px] before:top-1/2">TO</span>
                        <div className="flex items-center gap-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 p-1 rounded-md">
                          <input
                            type="color"
                            value={parseGradientColors(themeGradient, themeColor)[1]}
                            onChange={(e) => {
                              const [c1] = parseGradientColors(themeGradient, themeColor);
                              handleCustomGradient(c1, e.target.value);
                            }}
                            className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent p-0"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
